import React, { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Stack } from "@mui/material";
import { toast } from "react-toastify";
import { HttpStatusCode, isAxiosError } from "axios";
import { Button, ButtonVariantOption } from "@/components/Button";
import { usePropertyInfo } from "@/features/propertyCodeAcquisition/hooks/usePropertyInfo";
import { usePropertyCodePictureRequestList } from "@/features/propertyCodeAcquisition/hooks/usePropertyCodePictureRequestList";
import { PropertyInfoHeader } from "@/features/propertyCodeAcquisition/components/PropertyInfoHeader";
import { RefetchHistoryButton } from "@/features/propertyCodeAcquisition/components/RefetchHistoryButton";
import { PropertyCodePictureRequestSection } from "@/features/propertyCodeAcquisition/components/PropertyCodePictureRequestSection";
import { PropertyCodeAcquireDialog } from "@/features/propertyCodeAcquisition/components/PropertyCodeAcquireDialog";
import { parsePropertyCode } from "@/features/propertyCodeAcquisition/utils/parsePropertyCode";
import { usePropertyAcquisitionErrorReporting } from "@/features/propertyCodeAcquisition/hooks/usePropertyAcquisitionErrorReporting";
import {
  PropertyCodeAcquisitionPageLayout as PageLayout,
  PropertyCodeAcquisitionErrorView as ErrorView,
  PropertyCodeAcquisitionLoadingView as LoadingView,
} from "@/features/propertyCodeAcquisition/components/PropertyCodeAcquisitionStateViews";

/** 履歴の再取得に失敗したときのメッセージ。 */
const HISTORY_REFETCH_FAILED_MESSAGE =
  "取得履歴の更新に失敗しました。時間をおいて再取得してください。";

/**
 * 登記取得専用画面のコンテナ。
 * クエリパラメータ code（物件コード・UUID）を受け取り、物件情報・取得履歴を取得して表示する。
 * 全画面のローディング/エラーは物件情報（ヘッダー）系のみに限定し、履歴 API の状態は
 * PropertyCodePictureRequestSection 内で自己完結させる。
 */
export const PropertyCodeAcquisition: React.FC = () => {
  const [searchParams] = useSearchParams();
  // 書式不正・未指定時は API を発火させない（code=null）。
  const code = parsePropertyCode(searchParams.get("code"));

  const {
    propertyInfo,
    error: propertyError,
    mutate: mutateProperty,
  } = usePropertyInfo(code);
  const {
    history,
    isLoading: isHistoryLoading,
    isValidating: isHistoryValidating,
    error: historyError,
    mutate: mutateHistory,
  } = usePropertyCodePictureRequestList(code);

  // 取得ダイアログの開閉状態。取得操作は新規・再取得とも本ダイアログに集約する。
  const [isAcquireDialogOpen, setIsAcquireDialogOpen] = useState(false);
  // 取得成功ごとにインクリメントし、履歴テーブルのページを先頭へ戻すシグナルに使う。
  const [historyResetSignal, setHistoryResetSignal] = useState(0);

  // 物件情報の 404 判定（全画面「物件が見つかりません」分岐用）。
  const isPropertyNotFound =
    isAxiosError(propertyError) &&
    propertyError.response?.status === HttpStatusCode.NotFound;

  // エラー通知（Sentry 送信＋トースト）の副作用を集約 hook に委譲。
  usePropertyAcquisitionErrorReporting({
    propertyError,
    historyError,
    propertyInfo,
  });

  // 履歴の再取得（常設再取得ボタン・取得成功後）。失敗時はテーブルを維持しトーストで通知する。
  // RefetchHistoryButton 内部の useCallback 依存を毎回無効化しないよう安定参照にする。
  const refetchHistory = useCallback((): void => {
    void mutateHistory().catch(() => {
      toast.error(HISTORY_REFETCH_FAILED_MESSAGE);
    });
  }, [mutateHistory]);

  // 物件情報の再取得。失敗は propertyError 経由で usePropertyAcquisitionErrorReporting が
  // Sentry 送信＋トースト通知するため、ここでは未処理の拒否を防ぐためだけに握る（二重送信回避）。
  const reloadProperty = useCallback((): void => {
    void mutateProperty().catch(() => {
      // 通知は usePropertyAcquisitionErrorReporting に委ねるため握りつぶす。
    });
  }, [mutateProperty]);

  // 取得ダイアログの開閉・成功ハンドラ。PropertyCodeAcquireDialog の memo を効かせるため安定参照にする。
  const closeAcquireDialog = useCallback((): void => {
    setIsAcquireDialogOpen(false);
  }, []);

  const handleAcquireSuccess = useCallback((): void => {
    // 取得成功後に履歴を再取得し、取得完了行の DL アイコンを活性表示する。
    refetchHistory();
    // 取得直後は新着行を見せるためページを先頭へ戻す。
    setHistoryResetSignal((n) => n + 1);
    // あわせて物件情報も再取得し、初回取得による地番マスクを解除する。
    reloadProperty();
  }, [refetchHistory, reloadProperty]);

  // code 欠落・UUID 形式不正。地番・履歴を表示しない。
  if (code === null) {
    return (
      <PageLayout>
        <ErrorView message="物件コードが指定されていないか、形式が正しくありません。" />
      </PageLayout>
    );
  }

  // 全画面エラー/ローディングは「初回ロード（物件情報が未取得）」に限定する。
  // 一度取得に成功した後の再取得（取得成功直後の mutateProperty 等）が一時失敗しても、
  // 前回取得した（古い）propertyInfo を保持したまま画面（履歴テーブル含む）を維持し、通知はトーストに留める
  // （usePropertyAcquisitionErrorReporting）。課金済みの状態を全画面エラーで覆い隠して失わせない。
  if (propertyInfo === undefined) {
    // 物件情報が 404。
    if (isPropertyNotFound) {
      return (
        <PageLayout>
          <ErrorView message="物件が見つかりません。" />
        </PageLayout>
      );
    }

    // 物件情報のその他エラーは全画面エラー＋再読み込み（mutateProperty）。
    // 履歴エラーは全画面にせず PropertyCodePictureRequestSection 内で扱う。
    if (propertyError != null) {
      return (
        <PageLayout>
          <ErrorView
            message="データの取得に失敗しました。時間をおいて再度お試しください。"
            onReload={reloadProperty}
          />
        </PageLayout>
      );
    }

    // 初回ロード中（または未確定）は全画面ローディング。
    return (
      <PageLayout>
        <LoadingView />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Box data-testid="property-code-acquisition">
        {/* 物件情報（左）と操作ボタン群（右）を両端寄せ。 */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-end",
            gap: 2,
            mb: 3,
          }}
        >
          {/* 物件情報は左端から少し内側に寄せる（テーブルの左端は維持）。 */}
          <Box sx={{ ml: 2 }}>
            <PropertyInfoHeader propertyInfo={propertyInfo} />
          </Box>
          {/* 再取得（履歴の再フェッチ）と取得（新規取得ダイアログ）を右側にまとめる。 */}
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <RefetchHistoryButton
              isLoading={isHistoryLoading}
              isValidating={isHistoryValidating}
              onRefetch={refetchHistory}
            />
            <Button
              variant={ButtonVariantOption.Contained}
              label="不動産登記簿を取得する"
              onClick={() => {
                setIsAcquireDialogOpen(true);
              }}
            />
          </Stack>
        </Box>

        {/* 取得履歴セクション（ローディング/エラー/データの3状態を自己完結）。 */}
        <PropertyCodePictureRequestSection
          history={history}
          isLoading={isHistoryLoading}
          isValidating={isHistoryValidating}
          error={historyError}
          realEstateType={propertyInfo.realEstateType}
          resetPageSignal={historyResetSignal}
        />
      </Box>

      <PropertyCodeAcquireDialog
        open={isAcquireDialogOpen}
        propertyInfo={propertyInfo}
        onClose={closeAcquireDialog}
        onAcquireSuccess={handleAcquireSuccess}
      />
    </PageLayout>
  );
};
